import { Menu, Transition } from '@headlessui/react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useCallback, useState } from 'react';
import {
  downloadMsrChartImage,
  exportMsrChartImage,
  type MsrChartImageFormat,
} from './msrChartImageExport';

export interface MsrChartImageDownloadMenuProps {
  elementId: string;
  chartTitle: string;
  disabled?: boolean;
}

const MsrChartImageDownloadMenu: React.FC<MsrChartImageDownloadMenuProps> = ({
  elementId,
  chartTitle,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = useCallback(
    async (format: MsrChartImageFormat) => {
      if (loading || disabled) return;
      setLoading(true);
      try {
        const file = await exportMsrChartImage(elementId, format);
        if (!file) {
          throw new Error('Could not capture chart image. Ensure the chart is visible and try again.');
        }
        downloadMsrChartImage(file.blob, file.fileName);
      } finally {
        setLoading(false);
      }
    },
    [disabled, elementId, loading]
  );

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        type="button"
        disabled={disabled || loading}
        className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 bg-white/95 px-1.5 py-1 text-xs font-medium text-slate-600 shadow-sm backdrop-blur-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50/80 hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={`Download ${chartTitle} as image`}
        title="Download chart image"
      >
        {loading ? (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"
            aria-hidden
          />
        ) : (
          <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        <ChevronDownIcon className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-[80] mt-1 w-44 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-slate-950/5 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={`${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'} block w-full px-3 py-2 text-left text-xs`}
                onClick={() => void handleDownload('png')}
              >
                PNG (transparent)
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={`${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'} block w-full px-3 py-2 text-left text-xs`}
                onClick={() => void handleDownload('jpeg')}
              >
                JPEG
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default MsrChartImageDownloadMenu;
